import { useHead, useRoute } from '#imports'

import hashByRoute from '~/.translations/hash-by-route.json'
import keysByHash from '~/.translations/keys-by-hash.json'

import { PROCESS_KEY } from '../constants'
import { getTranslationsScript } from '../utils/getTranslationsScript'

import { useIsTranslationsSplitV2Enabled } from './useIsTranslationsSplitV2Enabled'
import { useVisitedRoutes } from './useVisitedRoutes'

/**
 * Example: 
 * hashByRoute = {
 *  "home": "83b9e990e5cee382a778d101ac838d2925bd84cd40eb0cf3c9e8812f5222c037",
 *  "product": "e8540a5e56cdfb25c45304bcf59eceaf992a761735cfdee9be24f59fc6c09ac9",
 *  "product-details": "e8540a5e56cdfb25c45304bcf59eceaf992a761735cfdee9be24f59fc6c09ac9",
 * "master-pp": "e8540a5e56cdfb25c45304bcf59eceaf992a761735cfdee9be24f59fc6c09ac9",
 * "landing": "193b43caf7a48144cac6f4a7d0fd8663cba69baf0cfc2760de74fc8030f466c7"
   ...
 * }

 * keysByHash = {
 *  "83b9e990e5cee382a778d101ac838d2925bd84cd40eb0cf3c9e8812f5222c037": ["home_meta_title_template", "button_cta", ...],
 *  "e8540a5e56cdfb25c45304bcf59eceaf992a761735cfdee9be24f59fc6c09ac9": ["button_cta", "product_title", ..],
 *  "193b43caf7a48144cac6f4a7d0fd8663cba69baf0cfc2760de74fc8030f466c7": ["filter_title", "sort_asc", ...],
 *  ...
 * }
 */

/**
 * From the server, it injects, in the HTML response, the script to populate the window.__BackmarketTranslations object which contains translations for the browser
 * If translations-split-v2 is enabled, it injects the translations of the current route (+ shared translations)
 * If not, it injects all the translations of the application
 * @param locale - fr-FR, en-US, etc.
 */
export function useInjectTranslationsScriptInHead(locale: string) {
  const translationsSplitV2Enabled = useIsTranslationsSplitV2Enabled()

  if (translationsSplitV2Enabled) {
    const route = useRoute()

    // if the route is not found, routeName is '__route-not-found__' for explicitness
    const routeName = String(route.name || '__route-not-found__')

    const hash = hashByRoute[routeName as keyof typeof hashByRoute]

    const { visitRoute } = useVisitedRoutes()
    const translationScript = getTranslationsScript({
      locale,
      hash,
      translationKeysByHash: keysByHash,
      hashByRoute,
    })

    visitRoute(hash)

    useHead({
      script: [
        {
          // after main entry file (seems to be 100)
          tagPriority: 110,
          key: 'translations',
          innerHTML: translationScript,
        },
      ],
    })
  } else {
    const translationScript =
      globalThis[PROCESS_KEY].translationsInlineCode[locale]

    if (translationScript === undefined) {
      throw new Error(
        '[nuxt-module-i18n] Cannot retrieve translations for current locale',
      )
    }
    useHead({
      script: [
        {
          // after main entry file (seems to be 100)
          tagPriority: 110,
          key: 'translations',
          innerHTML: translationScript,
        },
      ],
    })
  }
}
